import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IconButton, Paper } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { VideoUploadItem } from '../../../context/video-upload.types';
import { getUploadTotals } from '../../../context/video-upload-queue';
import {
  getUploadJobPhase,
  getVideoStatusDisplay,
} from '../../../common/utils/video-status-utils';
import { useUploadJobVideos } from '../../../hooks/use-upload-job-videos';
import { useVideoPermissions } from '../../../hooks/use-video-permissions';
import { useVideoUploads } from '../../../hooks/use-video-uploads';
import { DeleteVideoAction } from '../../../common/model/type/video-placement.types';
import OvCompactButton from '../atoms/OvCompactButton';
import OvVideoUploadProgress from '../atoms/OvVideoUploadProgress';
import OvVisuallyHidden from '../atoms/OvVisuallyHidden';
import OvDeleteVideoDialog from '../molecules/OvDeleteVideoDialog';
import OvVideoDeletedSnackbar from '../molecules/OvVideoDeletedSnackbar';
import OvVideoUploadTrayRow from '../molecules/OvVideoUploadTrayRow';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoUploadTrayProps {
  uploads: VideoUploadItem[];
  isExpanded: boolean;
  focusRequest: number;
  onToggle: () => void;
}

const OvVideoUploadTray: FC<OvVideoUploadTrayProps> = ({
  uploads,
  isExpanded,
  focusRequest,
  onToggle,
}) => {
  const { t } = useTranslation();
  const { canCreate, canUpdate, canDelete } = useVideoPermissions();
  const { retry, clearFinished } = useVideoUploads();
  const jobVideos = useUploadJobVideos(uploads);
  const [deleteTarget, setDeleteTarget] = useState<AdminVideo | null>(null);
  const [deleted, setDeleted] = useState<DeleteVideoAction | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (focusRequest) {
      headingRef.current?.focus();
    }
  }, [focusRequest]);

  const deletedSnackbar = (
    <OvVideoDeletedSnackbar action={deleted} onClose={() => setDeleted(null)} />
  );

  if (!uploads.length) {
    return deletedSnackbar;
  }

  const jobs = uploads.map((upload) => {
    const video = jobVideos.get(upload.videoId) ?? upload.video;

    return {
      upload,
      video,
      phase: getUploadJobPhase(getVideoStatusDisplay(video, upload)),
    };
  });
  const totals = getUploadTotals(uploads);
  const overallPercent = totals.totalBytes
    ? Math.round((totals.uploadedBytes / totals.totalBytes) * 100)
    : 0;
  const overallProgress = (
    <OvVideoUploadProgress
      label={t('videos.uploads.overallProgressLabel')}
      percent={overallPercent}
    />
  );
  const finishedCount = t('videos.uploads.finishedCount', {
    finished: totals.finished,
    total: uploads.length,
  });
  const failedCount = totals.failed > 0 && (
    <Failed>{t('videos.uploads.failedCount', { count: totals.failed })}</Failed>
  );

  return (
    <Tray
      elevation={6}
      role="region"
      aria-label={t('videos.uploads.trayLabel')}
    >
      <Header>
        <Heading ref={headingRef} tabIndex={-1}>
          {t('videos.uploads.trayLabel')}
        </Heading>
        <IconButton
          size="small"
          aria-expanded={isExpanded}
          aria-label={t(
            isExpanded ? 'videos.uploads.collapse' : 'videos.uploads.expand'
          )}
          onClick={onToggle}
        >
          {isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Header>

      {isExpanded ? (
        <>
          <Overview>
            {overallProgress}
            <span>{finishedCount}</span>
            {failedCount}
            {totals.pending > 0 && (
              <Hint>{t('videos.uploads.keepTabOpen')}</Hint>
            )}
            {totals.finished > 0 && (
              <div>
                <OvCompactButton tone="secondary" onClick={clearFinished}>
                  {t('videos.uploads.clearFinished')}
                </OvCompactButton>
              </div>
            )}
          </Overview>
          <List>
            {jobs.map(({ upload, video, phase }) => (
              <OvVideoUploadTrayRow
                key={upload.videoId}
                upload={upload}
                video={video}
                phase={phase}
                canRetryUpload={canCreate && canUpdate}
                canRetryProcessing={canUpdate}
                canDelete={canDelete}
                onRetryUpload={retry}
                onDelete={setDeleteTarget}
              />
            ))}
          </List>
        </>
      ) : (
        <Overview>
          <span>
            {totals.pending > 0
              ? t('videos.uploads.uploadingCount', { count: totals.pending })
              : finishedCount}
          </span>
          {totals.pending > 0 && overallProgress}
          {failedCount}
        </Overview>
      )}

      <OvVisuallyHidden aria-live="polite">
        {jobs.map(({ upload, video, phase }) => (
          <span key={upload.videoId}>
            {`${video.title}: ${t(`videos.uploads.phases.${phase}`)}. `}
          </span>
        ))}
      </OvVisuallyHidden>

      {deleteTarget && (
        <OvDeleteVideoDialog
          video={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={(action) => {
            setDeleteTarget(null);
            setDeleted(action);
          }}
        />
      )}
      {deletedSnackbar}
    </Tray>
  );
};

export default OvVideoUploadTray;

const Tray = styled(Paper)`
  && {
    position: fixed;
    right: ${Variables.spacing.MEDIUM};
    bottom: ${Variables.spacing.MEDIUM};
    z-index: 1300;
    width: min(24rem, calc(100vw - 2 * ${Variables.spacing.MEDIUM}));
    max-height: 60vh;
    display: flex;
    flex-direction: column;
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.WHITE};
    overflow: hidden;

    @media (max-width: ${breakpoints.lg}) {
      bottom: calc(
        ${Variables.layout.MOBILE_NAV_HEIGHT} + ${Variables.spacing.SMALL}
      );
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.5rem 0.5rem ${Variables.spacing.MEDIUM};
  background-color: ${Colours.OV_BASE};
  color: ${Colours.WHITE};

  .MuiIconButton-root {
    color: ${Colours.WHITE};
  }
`;

const Heading = styled.h2`
  margin: 0;
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;

  &:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }
`;

const Overview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: ${Variables.spacing.SMALL} ${Variables.spacing.MEDIUM};
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  font-size: ${Variables.fontSizes.SMALL};
`;

const Failed = styled.span`
  font-weight: bold;
  color: ${Colours.OV_ERROR_ACCESSIBLE};
`;

const Hint = styled.span`
  color: ${Colours.OV_CHARCOAL};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
`;
